import { useState, useRef, useEffect } from 'react';
import '../styles/landing.css';

// Punto final de la Cloud Function v2 (Firebase). Se puede sobreescribir con VITE_CHAT_ENDPOINT.
const CHAT_ENDPOINT = import.meta.env?.VITE_CHAT_ENDPOINT || 'https://us-central1-vortex-c9404.cloudfunctions.net/chatAgenteAscensos';

const QUICK_SUGGESTIONS = [
  '¿Cuánto cuesta la licencia?',
  '¿Cómo se activa la licencia?',
  '¿Qué incluye la app?',
  '¿Cómo cancelo mi suscripción y solicito reembolso?',
  '¿Tienen soporte?',
];

const WELCOME_MESSAGE =
  '¡Hola! 👋 Soy el asistente virtual de AscensosApp Vortex. ' +
  'Trabajo las 24 horas y puedo ayudarte con precios, licencias, temarios y soporte. ¿En qué te ayudo?';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'agent', text: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | error
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || status === 'sending') return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setStatus('sending');

    try {
      const res = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      const reply = data.reply || (data.error && `⚠️ ${data.error}`) || 'Lo siento, no pude generar una respuesta en este momento.';
      setMessages((prev) => [...prev, { role: 'agent', text: reply }]);
      setStatus('idle');
    } catch (err) {
      console.error('[ChatWidget] Error:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'agent', text: '⚠️ No logré conectar con el asistente. Inténtalo de nuevo en unos segundos.' },
      ]);
      setStatus('error');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="chat-widget">
      {/* Botón flotante */}
      <button
        type="button"
        className={`chat-widget-toggle${isOpen ? ' is-open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Cerrar chat' : 'Abrir chat'}
      >
        {isOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>

      {/* Ventana de chat */}
      {isOpen && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <span className="chat-widget-agent-avatar">⚓</span>
            <div>
              <strong>Asistente Vortex</strong>
              <span className="chat-widget-online">
                <span className="chat-widget-online-dot" /> En línea 24/7
              </span>
            </div>
            <button
              type="button"
              className="chat-widget-close"
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="chat-widget-body" ref={messagesRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg--${m.role}`}>
                {m.role === 'agent' && <span className="chat-msg-agent-badge">⚓</span>}
                <span className="chat-msg-bubble">{m.text}</span>
              </div>
            ))}
          </div>

          <div className="chat-widget-quick">
            {QUICK_SUGGESTIONS.map((q) => (
              <button key={q} type="button" onClick={() => sendMessage(q)}>
                {q}
              </button>
            ))}
          </div>

          <form className="chat-widget-form" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta..."
              disabled={status === 'sending'}
              autoComplete="off"
            />
            <button type="submit" disabled={status === 'sending' || !input.trim()}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}